import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Trophy, Medal, Star, CurrencyDollar, CheckCircle, X, Plus, PencilSimple, Trash, DownloadSimple, ChatText, Fire, Warning, ThumbsUp, CurrencyInr } from "@phosphor-icons/react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CHART_TOOLTIP_STYLE, CHART_TICK_XS } from "../config/uiHelpers";

const fmtInr = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN')}`;

const APPROVAL_FLOW = ["draft", "submitted", "bdm_approved", "manager_approved", "cfo_approved", "paid"];

const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  submitted: "bg-blue-50 text-blue-700 border-blue-200",
  bdm_approved: "bg-indigo-50 text-indigo-700 border-indigo-200",
  manager_approved: "bg-purple-50 text-purple-700 border-purple-200",
  cfo_approved: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  disputed: "bg-orange-50 text-orange-700 border-orange-200",
};

const NEXT_ACTION = {
  bde: { draft: "submitted", cfo_approved: null },
  bdm: { submitted: "bdm_approved" },
  manager: { submitted: "manager_approved", bdm_approved: "manager_approved" },
  cfo: { manager_approved: "cfo_approved", cfo_approved: "paid" },
  admin: {
    draft: "submitted", submitted: "bdm_approved", bdm_approved: "manager_approved",
    manager_approved: "cfo_approved", cfo_approved: "paid"
  },
};

const IncentiveCenter = ({ user, logout }) => {
  const role = user?.role || "bde";
  const [activeTab, setActiveTab] = useState(role === "admin" ? "approvals" : (role === "bde" ? "my" : "approvals"));
  const [dashboard, setDashboard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [rules, setRules] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [users, setUsers] = useState([]);
  const [shoutoutTarget, setShoutoutTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ruleEditor, setRuleEditor] = useState(null);

  const TABS = useMemo(() => {
    const t = [];
    if (role !== "customer") t.push({ key: "my", label: "My Earnings" });
    if (["admin", "manager", "bdm", "cfo"].includes(role)) t.push({ key: "approvals", label: "Approvals Queue" });
    t.push({ key: "leaderboard", label: "Leaderboard" });
    if (role === "admin") t.push({ key: "rules", label: "Commission Rules" });
    return t;
  }, [role]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [
        axios.get(`${API}/incentives/dashboard/${user.user_id}`).catch(() => ({ data: null })),
        axios.get(`${API}/incentives/entries?requester_role=${role}&requester_id=${user.user_id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/incentives/rules`).catch(() => ({ data: [] })),
        axios.get(`${API}/incentives/leaderboard`).catch(() => ({ data: [] })),
        role === "customer" ? Promise.resolve({ data: [] }) : api.get(`/users`).catch(() => ({ data: [] })),
      ];
      const [d, e, r, lb, u] = await Promise.all(calls);
      setDashboard(d.data);
      setEntries(e.data);
      setRules(r.data);
      setLeaderboard(lb.data);
      setUsers(u.data.filter(x => x.role !== "customer"));
    } catch (err) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [user.user_id, role]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const actOnEntry = async (entry, newStatus) => {
    try {
      await axios.put(`${API}/incentives/entries/${entry.entry_id}`, {
        status: newStatus, requester_role: role, requester_name: user.name, requester_id: user.user_id
      });
      toast.success(`Moved to ${newStatus.replace('_', ' ')}`);
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
  };

  const overrideAmount = async (entry, amount) => {
    try {
      await axios.put(`${API}/incentives/entries/${entry.entry_id}`, {
        commission_amount: Number(amount), requester_role: role, requester_name: user.name, requester_id: user.user_id
      });
      toast.success("Amount updated");
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const saveRule = async (rule) => {
    try {
      if (rule.rule_id) {
        await axios.put(`${API}/incentives/rules/${rule.rule_id}?requester_role=${role}`, rule);
        toast.success("Rule updated");
      } else {
        await axios.post(`${API}/incentives/rules?requester_role=${role}`, rule);
        toast.success("Rule created");
      }
      setRuleEditor(null);
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    try {
      await axios.delete(`${API}/incentives/rules/${id}?requester_role=${role}`);
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail); }
  };

  const downloadPayout = () => {
    const month = new Date().toISOString().slice(0, 7);
    window.open(`${API}/incentives/payout-export?month=${month}`, "_blank");
  };

  const sendShoutout = async (message) => {
    try {
      await axios.post(`${API}/incentives/shoutouts`, {
        to_user_id: shoutoutTarget.user_id, to_user_name: shoutoutTarget.user_name,
        message, from_user_id: user.user_id, from_user_name: user.name
      });
      toast.success("Shoutout sent!");
      setShoutoutTarget(null);
      fetchAll();
    } catch (err) { toast.error("Failed"); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8 max-w-6xl">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
            <div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Incentives</h1>
              <p className="text-base text-slate-700 mt-1">
                {role === "bde" ? "Track your commissions · submit claims · celebrate wins" : "Commission program · approvals · payouts"}
              </p>
            </div>
            {["admin", "cfo"].includes(role) && (
              <button onClick={downloadPayout} data-testid="payout-export-btn"
                className="rounded-none bg-slate-900 text-white hover:bg-[#002FA7] px-6 py-3 font-medium flex items-center gap-2">
                <DownloadSimple size={18} weight="bold" /> Export Payout CSV
              </button>
            )}
          </div>

          <div className="flex gap-1 mb-8 border-b border-slate-200 mt-6 overflow-x-auto">
            {TABS.map(t => (
              <button key={t.key} data-testid={`inc-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
                className={`px-5 py-3 text-sm font-medium transition-colors duration-300 border-b-2 -mb-px whitespace-nowrap ${activeTab === t.key ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><div className="loading-spinner"></div></div>
          ) : (
            <>
              {activeTab === "my" && <MyEarnings dashboard={dashboard} user={user} entries={entries.filter(e => e.user_id === user.user_id)} actOnEntry={actOnEntry} fmtInr={fmtInr} />}
              {activeTab === "approvals" && <ApprovalsQueue role={role} entries={entries} users={users} actOnEntry={actOnEntry} overrideAmount={overrideAmount} fmtInr={fmtInr} />}
              {activeTab === "leaderboard" && <Leaderboard board={leaderboard} onShoutout={setShoutoutTarget} fmtInr={fmtInr} />}
              {activeTab === "rules" && <Rules rules={rules} onEdit={setRuleEditor} onDelete={deleteRule} onCreate={() => setRuleEditor({})} fmtInr={fmtInr} />}
            </>
          )}
        </div>
      </div>

      {ruleEditor && <RuleEditor rule={ruleEditor} onSave={saveRule} onClose={() => setRuleEditor(null)} />}
      {shoutoutTarget && <ShoutoutModal target={shoutoutTarget} onSend={sendShoutout} onClose={() => setShoutoutTarget(null)} />}
    </div>
  );
};

/* -------- My Earnings (BDE + all users) -------- */
const MyEarnings = ({ dashboard, user, entries, actOnEntry, fmtInr }) => {
  if (!dashboard) return null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={CurrencyInr} label="This Month" value={fmtInr(dashboard.this_month)} color="#002FA7" />
        <StatCard icon={CurrencyInr} label="Year-to-Date" value={fmtInr(dashboard.ytd)} color="#10B981" />
        <StatCard icon={CheckCircle} label="Paid Out" value={fmtInr(dashboard.paid)} color="#059669" />
        <StatCard icon={Warning} label="Pending Approval" value={fmtInr(dashboard.pending)} color="#F59E0B" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>6-Month Paid Commission Trend</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={dashboard.trend_6m}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={CHART_TICK_XS} />
              <YAxis tick={CHART_TICK_XS} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [fmtInr(v), "Paid"]} />
              <Line type="monotone" dataKey="amount" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Recognition</h3>
          <div className="space-y-3">
            {(dashboard.badges || []).length === 0 ? <p className="text-sm text-slate-500">No badges yet — keep closing deals!</p> :
              dashboard.badges.slice(0, 5).map(b => (
                <div key={b.badge_id} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200">
                  <div className="text-2xl">{b.badge_icon || "🏆"}</div>
                  <div className="flex-1 text-sm">
                    <div className="font-bold">{b.badge_name}</div>
                    {b.description && <div className="text-xs text-slate-500">{b.description}</div>}
                  </div>
                </div>
              ))
            }
            {(dashboard.shoutouts || []).length > 0 && (
              <>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mt-3">Shoutouts received</div>
                {dashboard.shoutouts.slice(0, 3).map(s => (
                  <div key={s.shoutout_id} className="p-3 bg-blue-50 border border-blue-200 text-sm">
                    <div className="text-xs text-slate-500">from {s.from_user_name}</div>
                    <p>"{s.message}"</p>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>My Commission Entries</h3>
          <span className="text-xs text-slate-500">{entries.length} total</span>
        </div>
        {entries.length === 0 ? <p className="py-12 text-center text-sm text-slate-500">No entries yet. Commissions auto-created on Closed Won deals.</p> :
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Deal</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Deal Value</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Commission</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Status</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.entry_id} className="border-b border-slate-100">
                  <td className="px-5 py-3 text-sm">
                    <div className="font-semibold">{e.deal_title}</div>
                    <div className="text-xs text-slate-500">{(e.created_at || "").slice(0, 10)}</div>
                  </td>
                  <td className="px-5 py-3 text-sm text-right">{fmtInr(e.deal_value)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="font-bold text-[#002FA7]">{fmtInr(e.commission_amount)}</div>
                    <div className="text-xs text-slate-500">{e.commission_pct}% rate</div>
                  </td>
                  <td className="px-5 py-3"><span className={`inline-block px-2 py-0.5 border text-xs font-bold uppercase tracking-wider ${STATUS_BADGE[e.status]}`}>{(e.status || "").replace('_', ' ')}</span></td>
                  <td className="px-5 py-3 text-right">
                    {e.status === "draft" && <button data-testid={`submit-entry-${e.entry_id}`} onClick={() => actOnEntry(e, "submitted")} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-3 py-1.5 text-xs font-bold uppercase tracking-wider">Submit</button>}
                    {["submitted", "bdm_approved", "manager_approved"].includes(e.status) && (user.role === "bde" && e.user_id === user.user_id) && (
                      <button data-testid={`dispute-entry-${e.entry_id}`} onClick={() => actOnEntry(e, "disputed")} className="rounded-none border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wider">Dispute</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
};

/* -------- Approvals Queue -------- */
const ApprovalsQueue = ({ role, entries, actOnEntry, overrideAmount, fmtInr }) => {
  const toApprove = entries.filter(e => NEXT_ACTION[role]?.[e.status]);
  const other = entries.filter(e => !NEXT_ACTION[role]?.[e.status]);
  const [editing, setEditing] = useState(null);

  const confirmAction = (e) => {
    const next = NEXT_ACTION[role][e.status];
    if (!next) return;
    if (!window.confirm(`Move "${e.deal_title}" to ${next.replace('_', ' ')}?`)) return;
    actOnEntry(e, next);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200">
        <h3 className="text-lg font-semibold px-6 py-4 border-b border-slate-200" style={{fontFamily: 'Outfit, sans-serif'}}>
          Awaiting Your Approval <span className="text-sm text-slate-500 ml-2">({toApprove.length})</span>
        </h3>
        {toApprove.length === 0 ? <p className="py-12 text-center text-sm text-slate-500">Nothing awaiting your approval right now.</p> :
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Employee</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Deal</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Commission</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Status</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {toApprove.map(e => (
                <tr key={e.entry_id} className="border-b border-slate-100">
                  <td className="px-5 py-3">
                    <div className="font-semibold text-sm">{e.user_name}</div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">{e.user_role}</div>
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <div className="font-semibold">{e.deal_title}</div>
                    <div className="text-xs text-slate-500">{fmtInr(e.deal_value)} · {e.commission_pct}%</div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {role === "admin" && editing === e.entry_id ? (
                      <input type="number" step="0.01" autoFocus defaultValue={e.commission_amount}
                        onBlur={(ev) => { overrideAmount(e, ev.target.value); setEditing(null); }}
                        onKeyDown={(ev) => { if (ev.key === "Enter") ev.target.blur(); }}
                        className="w-28 rounded-none border border-[#002FA7] p-1 text-sm text-right" />
                    ) : (
                      <button data-testid={`amount-${e.entry_id}`} onClick={() => role === "admin" && setEditing(e.entry_id)}
                        className={`font-bold text-[#002FA7] ${role === "admin" ? "hover:underline" : ""}`}>
                        {fmtInr(e.commission_amount)}
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-3"><span className={`inline-block px-2 py-0.5 border text-xs font-bold uppercase tracking-wider ${STATUS_BADGE[e.status]}`}>{(e.status || "").replace('_', ' ')}</span></td>
                  <td className="px-5 py-3 text-right space-x-1">
                    <button data-testid={`approve-${e.entry_id}`} onClick={() => confirmAction(e)}
                      className="rounded-none bg-green-600 text-white hover:bg-green-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wider">
                      {NEXT_ACTION[role][e.status].replace('_', ' ')}
                    </button>
                    <button onClick={() => actOnEntry(e, "rejected")}
                      className="rounded-none border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wider">Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>

      {other.length > 0 && (
        <div className="bg-white border border-slate-200">
          <h3 className="text-sm font-semibold px-6 py-3 border-b border-slate-200 text-slate-500 uppercase tracking-wider">Other Entries ({other.length})</h3>
          <table className="w-full">
            <tbody>
              {other.slice(0, 20).map(e => (
                <tr key={e.entry_id} className="border-b border-slate-100">
                  <td className="px-5 py-3 text-sm">{e.user_name}</td>
                  <td className="px-5 py-3 text-sm">{e.deal_title}</td>
                  <td className="px-5 py-3 text-sm text-right font-bold text-[#002FA7]">{fmtInr(e.commission_amount)}</td>
                  <td className="px-5 py-3"><span className={`inline-block px-2 py-0.5 border text-xs font-bold uppercase tracking-wider ${STATUS_BADGE[e.status]}`}>{(e.status || "").replace('_', ' ')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* -------- Leaderboard -------- */
const Leaderboard = ({ board, onShoutout, fmtInr }) => {
  if (board.length === 0) return <p className="text-center py-12 text-slate-500">No earnings this month yet.</p>;
  return (
    <div className="space-y-3">
      {board.map(r => {
        const medal = r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : null;
        return (
          <div key={r.user_id} data-testid={`lb-${r.user_id}`}
            className={`flex items-center gap-4 bg-white border rounded-none p-5 transition-all duration-300 ${r.rank === 1 ? 'border-amber-400 shadow-md' : 'border-slate-200'}`}>
            <div className="text-3xl font-bold w-16 text-center" style={{fontFamily: 'Outfit, sans-serif'}}>
              {medal || `#${r.rank}`}
            </div>
            <div className="flex-1">
              <div className="font-bold text-lg">{r.user_name}</div>
              <div className="text-xs uppercase tracking-wider text-slate-500">{r.user_role} · {r.deals} {r.deals === 1 ? 'deal' : 'deals'}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-[#002FA7]" style={{fontFamily: 'Outfit, sans-serif'}}>{fmtInr(r.total)}</div>
              <div className="text-xs text-green-600">{fmtInr(r.paid)} paid</div>
            </div>
            <button data-testid={`shoutout-${r.user_id}`} onClick={() => onShoutout(r)}
              className="rounded-none border border-slate-300 hover:bg-slate-50 px-3 py-2 text-sm flex items-center gap-1">
              <ThumbsUp size={14} /> Shoutout
            </button>
          </div>
        );
      })}
    </div>
  );
};

/* -------- Rules (admin) -------- */
const Rules = ({ rules, onEdit, onDelete, onCreate }) => (
  <div className="space-y-4">
    <div className="flex justify-end">
      <button data-testid="create-rule-btn" onClick={onCreate}
        className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium flex items-center gap-2"><Plus size={16} /> New Rule</button>
    </div>
    {rules.length === 0 ? <p className="text-center py-12 text-slate-500">No rules yet.</p> :
      <div className="grid gap-3">
        {rules.map(r => (
          <div key={r.rule_id} data-testid={`rule-${r.rule_id}`}
            className="bg-white border border-slate-200 p-5 flex items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-bold">{r.name}</div>
                {r.active && <span className="text-[0.6rem] uppercase tracking-wider font-bold bg-green-50 text-green-700 border border-green-200 px-2 py-0.5">Live</span>}
              </div>
              <div className="text-sm text-slate-600">
                {r.applies_to_role.toUpperCase()} · {r.product_category} · {r.commission_pct}% base
                {r.tier_threshold > 0 && <> · +{r.tier_bonus_pct}% over ₹{Number(r.tier_threshold).toLocaleString('en-IN')}</>}
                {r.quota_monthly > 0 && <> · Quota ₹{Number(r.quota_monthly).toLocaleString('en-IN')}/mo</>}
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => onEdit(r)} className="p-2 hover:bg-slate-100"><PencilSimple size={16} /></button>
              <button onClick={() => onDelete(r.rule_id)} className="p-2 hover:bg-red-50 text-red-500"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    }
  </div>
);

/* -------- Rule Editor -------- */
const RuleEditor = ({ rule, onSave, onClose }) => {
  const [f, setF] = useState({
    name: rule?.name || "",
    applies_to_role: rule?.applies_to_role || "bde",
    product_category: rule?.product_category || "all",
    commission_pct: rule?.commission_pct ?? 5.0,
    tier_threshold: rule?.tier_threshold ?? 0,
    tier_bonus_pct: rule?.tier_bonus_pct ?? 0,
    quota_monthly: rule?.quota_monthly ?? 0,
    quota_bonus_flat: rule?.quota_bonus_flat ?? 0,
    active: rule?.active ?? true
  });
  const submit = (e) => { e.preventDefault(); onSave({ ...f, rule_id: rule?.rule_id }); };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">{rule?.rule_id ? "Edit" : "New"} Rule</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <input data-testid="rule-name" placeholder="Rule Name *" required value={f.name} onChange={e => setF({...f, name: e.target.value})} className="w-full rounded-none border border-slate-300 p-3 outline-none" />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.applies_to_role} onChange={e => setF({...f, applies_to_role: e.target.value})} className="rounded-none border border-slate-300 p-3 outline-none">
              <option value="bde">BDE</option><option value="bdm">BDM</option><option value="manager">Manager</option><option value="all">All Roles</option>
            </select>
            <select value={f.product_category} onChange={e => setF({...f, product_category: e.target.value})} className="rounded-none border border-slate-300 p-3 outline-none">
              <option value="all">All Categories</option><option value="camera">Camera</option><option value="alarm">Alarm</option><option value="access_control">Access Control</option>
            </select>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Base %</label>
              <input data-testid="rule-pct" type="number" step="0.01" value={f.commission_pct} onChange={e => setF({...f, commission_pct: parseFloat(e.target.value) || 0})} className="w-full rounded-none border border-slate-300 p-3 outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Tier Threshold (₹)</label>
              <input type="number" step="1000" value={f.tier_threshold} onChange={e => setF({...f, tier_threshold: parseFloat(e.target.value) || 0})} className="w-full rounded-none border border-slate-300 p-3 outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Tier Bonus %</label>
              <input type="number" step="0.01" value={f.tier_bonus_pct} onChange={e => setF({...f, tier_bonus_pct: parseFloat(e.target.value) || 0})} className="w-full rounded-none border border-slate-300 p-3 outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Quota / month (₹)</label>
              <input type="number" step="1000" value={f.quota_monthly} onChange={e => setF({...f, quota_monthly: parseFloat(e.target.value) || 0})} className="w-full rounded-none border border-slate-300 p-3 outline-none" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={e => setF({...f, active: e.target.checked})} /> Active</label>
        </div>
        <div className="border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-none border border-slate-300 px-5 py-2.5 hover:bg-slate-50">Cancel</button>
          <button type="submit" data-testid="rule-save" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-2.5 font-bold">Save</button>
        </div>
      </form>
    </div>
  );
};

/* -------- Shoutout Modal -------- */
const ShoutoutModal = ({ target, onSend, onClose }) => {
  const [msg, setMsg] = useState("");
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Send Shoutout</div>
            <div className="font-bold">{target.user_name}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-6">
          <textarea autoFocus rows={4} placeholder="Great job closing the XYZ deal!" value={msg} onChange={e => setMsg(e.target.value)}
            className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
          <button onClick={() => msg.trim() && onSend(msg)} data-testid="send-shoutout"
            className="mt-4 w-full rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-bold">Send</button>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-slate-200 p-5">
    <Icon size={22} weight="duotone" style={{color}} className="mb-2" />
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-2xl font-bold mt-1" style={{fontFamily: 'Outfit, sans-serif', color}}>{value}</div>
  </div>
);

export default IncentiveCenter;
